import { Head, router, usePage } from "@inertiajs/react"
import { useState } from "react"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import InviteUser from "@/components/users/invite-user"
import { UserRow } from "@/components/users/user-row"
import AppLayout from "@/layouts/app-layout"
import SettingsLayout from "@/layouts/settings/layout"
import {
  settingsInvitationRemovalsPath,
  settingsInvitationResendsPath,
  settingsUserPath,
  settingsUsersPath,
} from "@/routes"
import type { Account, BreadcrumbItem, User } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Members",
    href: settingsUsersPath(),
  },
]

export default function Account() {
  const { users } = usePage<{ users: User[] }>().props as { users: User[] }
  const { auth } = usePage().props
  const currentUser = auth.user
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleInvitationRevoke = (user: User) => {
    router.post(settingsInvitationRemovalsPath(), { id: user.uid })
    toast("Invitation revoked")
    router.reload()
  }

  const handleResend = (user: User) => {
    router.post(settingsInvitationResendsPath(), { user_id: user.uid })
    toast("Invitation resent")
    router.reload()
  }

  const handleUserSelectedForDelete = (user: User) => {
    setSelectedUser(user)
    setShowDeleteDialog(true)
  }

  const handleUserDelete = (user: User | null) => {
    if (!user) return
    router.delete(settingsUserPath({ id: user.uid }))
    setShowDeleteDialog(false)
    setSelectedUser(null)
    router.reload()
  }

  const userRows = users.map((user) => (
    <UserRow
      key={user.id}
      user={user}
      onDelete={(user) => handleInvitationRevoke(user)}
      onResend={(user) => handleResend(user)}
      onUserSelectedForDelete={handleUserSelectedForDelete}
      currentUser={currentUser}
    />
  ))
  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={breadcrumbs[breadcrumbs.length - 1].title} />

      <SettingsLayout>
        <div className="space-y-6">
          <InviteUser />
          <div className="space-y-2">
            <h3 className="text-sm font-medium">Members</h3>
            <div className="divide-y divide-gray-200">{userRows}</div>
          </div>
        </div>

        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove member</AlertDialogTitle>
              <AlertDialogDescription>
                This will remove {selectedUser?.display_name} from the team. You
                can invite them back at any time.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => handleUserDelete(selectedUser)}
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                Remove member
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SettingsLayout>
    </AppLayout>
  )
}
